import { useForm } from 'react-hook-form'
import { AdSupplier, CreateAdSupplier } from '@magnetar/admin-api-interfaces'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { logger } from '@magnetar/utils'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { adSupplierService } from '@/services/ad_supplier_service'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useViewMode } from '@/stores/viewMode'
import { useMe } from '@/hooks/useMe'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { AllowedAction } from '@/data/allowedActions'
import { SLOTS_PER_HOUR_HINT, SLOTS_PER_HOUR_MAX, SLOTS_PER_HOUR_MIN, parseSlotsInput, slotsToInput } from './slotsPerHour'

interface AdSupplierFormValues {
  supplier_name: string
  manager: string
  phone: string
  address: string
  is_active: boolean
  /** 梯次數量（每小時）；表單用字串，空字串＝未設定 */
  slots_per_hour: string
}

interface AdSupplierInfoProps {
  adSupplier?: AdSupplier
  isLoading?: boolean
  mode: 'create' | 'edit'
}

export default function AdSupplierInfo({ adSupplier, isLoading, mode }: AdSupplierInfoProps) {
  const { token } = useAuth()
  const navigate = useNavigate()
  const { mode: viewMode, id: viewModeId } = useViewMode()
  const { user } = useMe()
  const { hasPermission } = useMyPermissions()
  // 梯次數量只有管理端能設：sys_admin 或組織的供應商管理權；廣告商自己的帳號只能看
  const canEditSlots =
    user?.role_type === 'sys_admin' ||
    hasPermission('system_provider', AllowedAction.U) ||
    hasPermission('system_ad_supplier', AllowedAction.U) ||
    hasPermission('provider_supplier_management', AllowedAction.U)
  const form = useForm<AdSupplierFormValues>({
    defaultValues: {
      supplier_name: '',
      manager: '',
      phone: '',
      address: '',
      is_active: true,
      slots_per_hour: '',
    },
  })

  // Update form values when adSupplier data changes
  useEffect(() => {
    if (adSupplier && mode === 'edit') {
      form.reset({
        supplier_name: adSupplier.supplier_name,
        manager: adSupplier.manager,
        phone: adSupplier.phone,
        address: adSupplier.address ?? '',
        is_active: adSupplier.is_active,
        slots_per_hour: slotsToInput(adSupplier.slots_per_hour),
      })
    }
  }, [adSupplier, form, mode])

  async function onSubmit(data: AdSupplierFormValues) {
    // 沒有管理端權限就不帶這個欄位（後端對不同值會 403）
    const slots = parseSlotsInput(data.slots_per_hour)
    const slotsPatch = canEditSlots && slots.ok ? { slots_per_hour: slots.value } : {}
    try {
      if (mode === 'create') {
        const createData: CreateAdSupplier = {
          supplier_name: data.supplier_name,
          manager: data.manager,
          phone: data.phone,
          address: data.address,
          is_active: data.is_active,
          ...slotsPatch,
          // sys_admin 在 provider mode 時需要帶入 provider_id
          // provider_admin 可由 backend 自動取得
          ...(viewMode === 'provider' && viewModeId ? { provider_id: viewModeId } : {}),
        }
        const response = await adSupplierService.createAdSupplier(createData, token)
        if (response.success) {
          toast.success('供應商建立成功')
          navigate(-1)
        } else {
          toast.error('供應商建立失敗')
        }
      } else {
        const updateData = {
          supplier_name: data.supplier_name,
          manager: data.manager,
          phone: data.phone,
          address: data.address,
          is_active: data.is_active,
          ...slotsPatch,
        }
        const response = await adSupplierService.updateAdSupplier(
          adSupplier!.supplier_id,
          updateData,
          token,
        )
        if (response.success) {
          toast.success('供應商更新成功')
        } else {
          toast.error('供應商更新失敗')
        }
      }
    } catch (error) {
      logger.error('Error submitting form:', error)
      toast.error('操作失敗，請稍後再試')
    }
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>基本資料</CardTitle>
            <CardDescription>供應商的基本資訊</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6">
            <FormField
              control={form.control}
              name="supplier_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>供應商名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入供應商名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="manager"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>負責人</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入負責人姓名" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>電話</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入電話" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>地址</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入地址" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="slots_per_hour"
              rules={{
                validate: (value) => {
                  const parsed = parseSlotsInput(value)
                  return parsed.ok || parsed.message
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>梯次數量（每小時）</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={SLOTS_PER_HOUR_MIN}
                      max={SLOTS_PER_HOUR_MAX}
                      step={1}
                      placeholder="未設定（通常是 12）"
                      disabled={!canEditSlots}
                      className="max-w-xs tabular-nums"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {canEditSlots
                      ? `每小時有幾個廣告梯次。${SLOTS_PER_HOUR_HINT}`
                      : '由管理端設定，廣告商帳號只能查看。'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>啟用狀態</FormLabel>
                    <FormDescription>設定供應商是否啟用</FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <Button type="submit">{mode === 'create' ? '建立供應商' : '更新供應商'}</Button>
      </form>
    </Form>
  )
}
